import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';

import { Action, select, Store } from '@ngrx/store';
import { settingsSelectors, State, User, UserRole, UserType } from '@wam/shared';
import { AuthenticationService } from '../authentication.service';
import { ConfigurationInitializerService } from '@app/configuration/configuration-initializer.service';
import { authenticationSelectors } from '@wam/authentication';
import * as authenticationActions from './authentication.actions';
import { isNil } from 'lodash-es';
import * as profileActions from '../../profile/state/profile.actions';
import { MessagesAndNotificationsInitializerService } from '@app/messages-and-notifications/messages-and-notifications-initializer.service';
import { BatchService } from '@app/organizations/batch.service';
import { INITIAL_PROGRAMS_DATA_LOCAL_STORAGE_KEY } from '@app/constants';
import { OrgUpdatesLockingInitializerService } from '@app/shared/org-updates-locking-initializer.service';
import { AhaPoll, AhaService } from '@app/shared/aha.service';
import { ApiGatewayServiceV2 } from '../api-gateway-v2/api-gateway-v2.service';
import { isReportsApp } from '@app/reports-app/reports-app.util';
import { ReportsAppAccessService } from '@app/reports-app/reports-app-access.service';

@Injectable()
export class AuthenticationEffects {
  private actions$ = inject(Actions);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private configurationInitializerService = inject(ConfigurationInitializerService);
  private messagesAndNotificationsInitializerService = inject(
    MessagesAndNotificationsInitializerService,
  );
  private goalsInitializerService = inject(OrgUpdatesLockingInitializerService);
  private store = inject<Store<State>>(Store);
  private batchService = inject(BatchService);
  private ahaService = inject(AhaService);
  private readonly apiGatewayServiceV2 = inject(ApiGatewayServiceV2);
  private readonly reportsAppAccessService = inject(ReportsAppAccessService);

  logIn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.logIn),
        mergeMap(() => this.authenticationService.logIn()),
      ),
    { dispatch: false },
  );

  signUp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.signUp),
        mergeMap(() => this.authenticationService.signUp()),
      ),
    { dispatch: false },
  );

  getReadOnly$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.getReadOnly),
      mergeMap(() => this.store.pipe(select(authenticationSelectors.getCurrentUser)).pipe(take(1))),
      mergeMap(({ organization, type }) =>
        this.authenticationService.getReadOnly(organization, type),
      ),
      map((readOnly) => authenticationActions.getReadOnlySuccess({ readOnly })),
    ),
  );

  getReadOnlySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.getReadOnlySuccess),
        tap(({ readOnly }) => localStorage.setItem('readOnly', `${readOnly}`)),
      ),
    { dispatch: false },
  );

  authorizeWithTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.authorizeWithTokens),
      tap(({ logoutUrl, includeLogoutToken }) => {
        if (!isNil(logoutUrl)) {
          localStorage.setItem('logoutUrl', logoutUrl);
        }
        if (!isNil(includeLogoutToken)) {
          localStorage.setItem('includeLogoutToken', `${includeLogoutToken}`);
        }
      }),
      withLatestFrom(this.store.pipe(select(settingsSelectors.getUserType)).pipe(take(1))),
      map(([{ idToken, refreshToken }, userType]) =>
        this.authenticationService.buildUser(idToken, refreshToken, userType),
      ),
      tap((user: User) => localStorage.setItem('currentUser', JSON.stringify(user))),
      mergeMap((user: User) => [
        authenticationActions.updateCurrentUser({ user }),
        authenticationActions.getReadOnly(),
      ]),
    ),
  );

  updateCurrentUser$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(authenticationActions.updateCurrentUser),
        tap(({ user: { roles } }) => {
          // District admin rostering provider detection has no place in the standalone reports
          // app - it has no rostering UI, and its users (e.g. `ext-parent`) are never district
          // admins anyway, but this stays explicit rather than relying on that role never
          // matching.
          if (roles?.includes(UserRole.DISTRICT_ADMIN) && !isReportsApp()) {
            this.store.dispatch(authenticationActions.determineRosteringProvider());
          }
        }),
        mergeMap((refreshed) => this.apiGatewayServiceV2.loadRegistry().pipe(map(() => refreshed))),
        mergeMap((refreshed) =>
          this.configurationInitializerService.initialize().pipe(map(() => refreshed)),
        ),
        mergeMap(({ refreshed }) =>
          this.messagesAndNotificationsInitializerService.initialize().pipe(
            mergeMap(() => this.goalsInitializerService.initialize()),
            map(() => refreshed),
          ),
        ),
        mergeMap((refreshed) => this.ahaService.load().pipe(map(() => refreshed))),
        tap((refreshed) => {
          if (!refreshed) {
            if (!isNil(localStorage.getItem(INITIAL_PROGRAMS_DATA_LOCAL_STORAGE_KEY))) {
              const { language } = JSON.parse(
                localStorage.getItem(INITIAL_PROGRAMS_DATA_LOCAL_STORAGE_KEY),
              );
              this.store.dispatch(profileActions.updateLanguage({ language }));
            }
          }
        }),
        tap((refreshed) => {
          if (!refreshed) {
            this.router.navigateByUrl('/');
          }
        }),
      )
      .pipe(
        map(() => authenticationActions.logInSuccess()),
        catchError((error): Observable<Action> => {
          if (!isReportsApp()) {
            return throwError(() => error);
          }

          // The standalone reports app authorizes from a single, non-refreshable id token (see
          // `ReportsAppAccessService`). If any of the bootstrap requests above fail - most commonly
          // a 401, because a downstream service rejected the token even though it looked
          // unexpired/well-formed client-side - there is nothing to retry. Without this, the
          // uncaught error would otherwise kill this effect for the rest of the page's lifetime,
          // leaving `ReportsAppAccessService.authorize()`'s bootstrap race with nothing to resolve
          // it except its 20s timeout fallback - and once that fires, `ReportsAppComponent` would
          // still attempt `loadReports()` (which fails the same way) before finally showing the
          // generic "report not available" message. Resolve immediately instead, with an honest
          // "we could not verify access" message.
          this.reportsAppAccessService.setInitializationFailed();
          return of(authenticationActions.logInSuccess());
        }),
      ),
  );

  updateClientId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.updateClientId),
        tap(({ clientId }) => localStorage.setItem('clientId', clientId)),
      ),
    { dispatch: false },
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.refreshToken),
      mergeMap(() =>
        this.authenticationService.refreshToken().pipe(
          tap((user: User) => localStorage.setItem('currentUser', JSON.stringify(user))),
          map((user: User) => authenticationActions.updateCurrentUser({ user, refreshed: true })),
          catchError(() =>
            of(authenticationActions.refreshTokenFailed(), authenticationActions.logOut()),
          ),
        ),
      ),
    ),
  );

  logInSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.logInSuccess),
        mergeMap(() => this.store.pipe(select(authenticationSelectors.getCurrentUser), take(1))),
        tap((user: User) => {
          // The standalone reports app forces `user.type` to `TEACHER` regardless of the actual
          // role (see `ReportsAppAccessService.authorizeFromToken()`), so this needs an explicit
          // check - it would otherwise show the teacher feedback poll to e.g. an `ext-parent`.
          // `AhaService.showPoll()` already no-ops there too (the widget script is never loaded -
          // see `AhaService.load()`), but this avoids relying on that alone.
          if (user.type === UserType.TEACHER && !isReportsApp()) {
            if (user.roles.includes(UserRole.DISTRICT_ADMIN)) {
              this.ahaService.showPoll(AhaPoll.AHA_DISTRICT_ADMIN_FEEDBACK_POLL);
            } else if (user.roles.includes(UserRole.SCHOOL_ADMIN)) {
              this.ahaService.showPoll(AhaPoll.AHA_SCHOOL_ADMIN_FEEDBACK_POLL);
            } else {
              this.ahaService.showPoll(AhaPoll.AHA_TEACHER_FEEDBACK_POLL);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticationActions.logOut),
        mergeMap(() => this.router.navigateByUrl('/logout')),
      ),
    { dispatch: false },
  );

  performLogoutSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.performLogoutSteps),
      tap(() => {
        localStorage.clear();
      }),
      mergeMap(() => this.authenticationService.logOut()),
      mergeMap(() => [authenticationActions.clearNgRxStore()]),
    ),
  );

  getOrgCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.getOrgCode),
      mergeMap(() =>
        this.authenticationService
          .getOrgCode()
          .pipe(map((orgCode) => authenticationActions.getOrgCodeSuccess({ orgCode }))),
      ),
      catchError(() => of(authenticationActions.getOrgCodeFailure())),
    ),
  );

  determineRosteringProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationActions.determineRosteringProvider),
      mergeMap(() =>
        this.batchService.getRosteringProvider().pipe(
          map((rosteringProvider) =>
            authenticationActions.determineRosteringProviderSuccess({ rosteringProvider }),
          ),
          catchError((error) =>
            of(authenticationActions.determineRosteringProviderFailed({ error })),
          ),
        ),
      ),
    ),
  );
}
